/* eslint-disable import/prefer-default-export */
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

/**
 * An error raised by `tryToCatchApiCall`, carrying the failed response's HTTP status.
 *
 * The status is what lets a caller tell a refusal apart from a glitch — a 4xx will answer the
 * same way however many times it is asked, a 5xx may not. Without it every failure looks alike.
 */
export interface ApiCallError extends Error {
    status?: number;
}

/**
 * Runs an API call, reports a failure to RUM, and rethrows it as an `ApiCallError` carrying the
 * server's message and the response's HTTP status.
 *
 * Generic in the call's own return type so callers get it back. It used to be typed as returning
 * `unknown`, which is why every consumer of the Gusto store saw `{}` where a payroll should be —
 * `response.status` and `payroll.payroll_uuid` were unreachable on their own results.
 */
export const tryToCatchApiCall = async <T, Args extends unknown[] = []>(
    fn: (...args: Args) => Promise<T>,
    action = '',
    ...args: Args
): Promise<T> => {
    try {
        // eslint-disable-next-line @typescript-eslint/return-await
        return await fn(...args);
    } catch (error: any) {
        sendDatadogRumError(`Error in tryToCatchApiCall while ${action || fn.name}`, error, {
            source: 'tryToCatchApiCall',
            action,
            functionName: fn.name,
        });
        const actionMessage = action ? `while ${action}` : `on ${fn.name}`;

        let errorMessage = `An error occurred ${actionMessage}. Please try again later.`;

        if (error.request?.responseType === 'blob') {
            const reader = new FileReader();
            reader.readAsText(error.response.data, 'utf-8');
            const text = await new Promise((resolve) => {
                reader.onload = () => {
                    resolve(reader.result);
                };
            });
            errorMessage = text as string;
        } else if (error.response && error.response.data) {
            errorMessage = JSON.stringify(error.response.data);
        }

        const apiCallError: ApiCallError = new Error(errorMessage);
        apiCallError.status = error.response?.status;

        throw apiCallError;
    }
};
